import { useState } from 'react';
import { toast } from 'sonner';

import {
  connectLinkedIn,
  fetchLinkedInAccountDetails,
  reconnectLinkedIn,
  registerLinkedInWebhook,
} from '@/hooks/user';

export const useLinkedInPopup = () => {
  const [loading, setLoading] = useState(false);
  const [messageHandled, setMessageHandled] = useState(false);

  const openLinkedInPopup = async (isReconnect = false) => {
    setLoading(true);
    setMessageHandled(false); // Reset message handled state

    try {
      const response = isReconnect
        ? await reconnectLinkedIn('popup')
        : await connectLinkedIn('popup');
      if (response.url) {
        // Create iframe popup
        const popup = window.open(
          '',
          'linkedin-connect',
          'width=600,height=700,scrollbars=yes,resizable=yes'
        );

        if (popup) {
          popup.document.write(`
            <!DOCTYPE html>
            <html>
              <head>
                <title>Connect LinkedIn</title>
                <style>
                  body { 
                    margin: 0; 
                    padding: 0; 
                    font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, sans-serif; 
                  }
                  iframe { 
                    width: 100%; 
                    height: 100vh; 
                    border: none; 
                  }
                  .loading { 
                    display: flex; 
                    justify-content: center; 
                    align-items: center; 
                    height: 100vh; 
                    background: #f8f9fa;
                    flex-direction: column;
                    gap: 16px;
                  }
                  .spinner {
                    width: 40px;
                    height: 40px;
                    border: 4px solid #e3e3e3;
                    border-top: 4px solid #0077b5;
                    border-radius: 50%;
                    animation: spin 1s linear infinite;
                  }
                  @keyframes spin {
                    0% { transform: rotate(0deg); }
                    100% { transform: rotate(360deg); }
                  }
                </style>
              </head>
              <body>
                <div class="loading" id="loading">
                  <div class="spinner"></div>
                  <p>Connecting to LinkedIn...</p>
                </div>
                <iframe 
                  src="${response.url}" 
                  id="linkedin-iframe"
                  style="display: none;"
                  onload="document.getElementById('loading').style.display='none'; this.style.display='block';"
                ></iframe>
                <script>
                  // Listen for messages from the iframe
                  window.addEventListener('message', function(event) {
                    if (event.origin !== window.location.origin) return;
                    
                    if (event.data.type === 'LINKEDIN_CONNECTED') {
                      
                      // Add reconnect flag to the message
                      const messageWithReconnectFlag = {
                        ...event.data,
                        isReconnect: ${isReconnect}
                      };
                      
                      // Forward the message to the main application window                      
                      if (window.opener && !window.opener.closed) {
                        window.opener.postMessage(messageWithReconnectFlag, window.location.origin);
                      } else {
                        console.error('Popup: Cannot forward message - no valid opener');
                      }
                                            
                      // Close popup after delay
                      setTimeout(function() {
                        window.close();
                      }, 3000);
                    }
                  });
                </script>
              </body>
            </html>
          `);

          // Monitor popup for completion (manual closes only)
          const checkClosed = setInterval(() => {
            if (popup.closed) {
              clearInterval(checkClosed);
              setLoading(false);
              // Only reload if popup was closed manually (not by message handler)
              if (!messageHandled) {
                window.location.reload();
              }
            }
          }, 1000);

          // Handle successful connection from popup
          const handleMessage = async (event) => {
            if (event.origin !== window.location.origin) return;

            if (event.data.type === 'LINKEDIN_CONNECTED') {
              console.log('Main app: Received message from popup:', event.data);
              console.log('Main app: Account ID:', event.data.accountId);
              setMessageHandled(true); // Mark that message was handled
              setLoading(false);

              if (event.data.success) {
                toast.success(
                  event.data.message || 'LinkedIn connected successfully!'
                );

                // Only fetch account details and register webhook for new connections, not reconnects
                if (
                  event.data.linkedinStatus === 'success' &&
                  !event.data.isReconnect
                ) {
                  try {
                    console.log('Fetching LinkedIn account details');
                    await fetchLinkedInAccountDetails(event.data.accountId);
                    console.log('Registering LinkedIn webhook');
                    await registerLinkedInWebhook();
                    console.log('LinkedIn webhook registered successfully');
                  } catch (error) {
                    console.error(
                      'Failed to register LinkedIn webhook:',
                      error
                    );
                    // Don't fail the entire flow if webhook registration fails
                  }
                } else if (event.data.isReconnect) {
                  console.log(
                    'Skipping account details fetch and webhook registration for reconnect'
                  );
                }

                // Refresh user data to get updated connection status AFTER webhook registration
                window.location.reload();
              } else {
                toast.error(event.data.message || 'LinkedIn connection failed');
              }
            }
          };

          window.addEventListener('message', handleMessage);

          // Clean up event listener when popup closes
          const checkClosedWithCleanup = setInterval(() => {
            if (popup.closed) {
              clearInterval(checkClosed);
              clearInterval(checkClosedWithCleanup);
              window.removeEventListener('message', handleMessage);
              setLoading(false);
            }
          }, 1000);
        } else {
          toast.error('Popup blocked. Please allow popups for this site.');
          setLoading(false);
        }
      } else {
        console.error('No redirect URL received from backend.');
        toast.error('Failed to get LinkedIn connection URL');
        setLoading(false);
      }
    } catch (error) {
      console.error('LinkedIn connection error:', error);
      toast.error('Failed to connect LinkedIn. Please try again.');
      setLoading(false);
    }
  };

  return { openLinkedInPopup, loading };
};
